import api from "@/services/api";

export default {
  namespaced: true, // Name of namespace exists here '@/store/namespaces'
  state: {
    accounts: [],
    selectedAccount: null,
    loadingAccounts: false,
    loadingAcctsByUid: {},
    acctsByUid: {},
  },
  getters: {
    getAccounts: (state) => {
      return state.accounts;
    },
    getSelectedAccount: (state) => {
      return state.selectedAccount;
    },
    getSelectedAcctUid: (state) => {
      return state.selectedAccount?.acct_uid || null;
    },
    getLoadingAccounts: (state) => {
      return state.loadingAccounts;
    },
    getLoadingAcctsByUid: (state) => {
      return state.loadingAcctsByUid;
    },
    getLoadingAcctByUid: (state) => (acctUid) => {
      return Boolean(state.loadingAcctsByUid[acctUid]);
    },
    getAcctsByUid: (state) => {
      return state.acctsByUid;
    },
    getAcctByUid: (state) => (acctUid) => {
      return state.acctsByUid[acctUid] || null;
    },
  },
  mutations: {
    setAccounts: (state, accounts) => {
      state.accounts = accounts;
    },
    setSelectedAccount: (state, account) => {
      state.selectedAccount = account;
    },
    setLoadingAccounts: (state, loading) => {
      state.loadingAccounts = loading;
    },
    setLoadingAcctByUid: (state, { acctUid, loading }) => {
      state.loadingAcctsByUid = {
        ...state.loadingAcctsByUid,
        [acctUid]: loading,
      };
    },
    setAcctByUid: (state, { acctUid, account }) => {
      state.acctsByUid = {
        ...state.acctsByUid,
        [acctUid]: account,
      };
    },
  },
  actions: {
    async fetchUserAccounts({ getters, commit }, userUid) {
      try {
        commit("setLoadingAccounts", true);
        if (!userUid) {
          console.warn(
            "Missing user UID while fetching user account. Skipping request."
          );
          return null;
        }

        if (getters.getSelectedAcctUid && getters.getAccounts.length > 0) {
          return getters.getSelectedAccount;
        }

        const response = await api.get(`/user-accounts/${userUid}/accounts`);

        const userAccountsData = Array.isArray(response?.data)
          ? response.data
          : [];

        // Extract the actual accounts from the response structure
        const accounts = userAccountsData
          .map((accountData) => accountData?.item)
          .filter(Boolean);

        if (accounts.length === 0) {
          console.warn(
            "User accounts response did not include any valid accounts.",
            response
          );
          commit("setAccounts", []);
          commit("setSelectedAccount", null);
          return null;
        }

        // Store all accounts
        commit("setAccounts", accounts);

        // Find and set the default account as selected
        const defaultAccount = accounts.find(
          (account) => account.default === true
        );

        if (defaultAccount) {
          commit("setSelectedAccount", defaultAccount);
          return defaultAccount;
        } else {
          // If no default account, select the first one
          commit("setSelectedAccount", accounts[0]);
          console.warn(
            "No default account found, using first account as selected."
          );
          return accounts[0];
        }
      } catch (error) {
        console.error("Error fetching user account:", error);
        commit("setAccounts", []);
        commit("setSelectedAccount", null);
        return null;
      } finally {
        // commit("setLoadingAccounts", false);
        setTimeout(() => {
          commit("setLoadingAccounts", false);
        }, 1000);
      }
    },
    async fetchAcctByUid(
      { getters, commit, dispatch },
      { userUid, acctUid, force = false }
    ) {
      try {
        const loading = getters.getLoadingAcctByUid(acctUid);
        if (loading) return;

        if (!acctUid) {
          console.warn(
            "Missing account UID while fetching account details. Skipping request."
          );
          return null;
        }

        if (!force && getters.getAcctByUid(acctUid)) {
          return getters.getAcctByUid(acctUid);
        }

        let resolvedUserUid = userUid;
        if (!resolvedUserUid) {
          const userInfo = await dispatch("userSession/getUserInfo", null, {
            root: true,
          });
          resolvedUserUid = userInfo?.id;
        }

        if (!resolvedUserUid) {
          console.warn(
            "Missing user UID while fetching account details. Skipping request."
          );
          return null;
        }

        commit("setLoadingAcctByUid", { acctUid, loading: true });

        const response = await api.get(
          `/user-accounts/${resolvedUserUid}/accounts/${acctUid}`
        );

        const account = response?.data?.item || null;
        if (!account) {
          console.warn(
            "Account response did not include an account payload.",
            response
          );
          return null;
        }

        commit("setAcctByUid", { acctUid, account });
        return account;
      } catch (error) {
        console.error(`Error fetching account ${acctUid}:`, error);
        return null;
      } finally {
        if (acctUid) {
          commit("setLoadingAcctByUid", { acctUid, loading: false });
        }
      }
    },
  },
};
